import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Search, CheckCircle, XCircle, Eye, ChevronLeft, ChevronRight,
  Shield, Loader2, MapPin, Phone, Star, Clock, Award, Filter,
  UserCheck, AlertTriangle, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VERIFICATION_LEVELS = [
  { value: '', label: 'All Levels' },
  { value: 'none', label: 'Not Verified' },
  { value: 'identity', label: 'Identity Submitted' },
  { value: 'fully_verified', label: 'Fully Verified' }
];

export default function AdminPanditsPage({ admin }) {
  const [pandits, setPandits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [verificationLevel, setVerificationLevel] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Modal states
  const [selectedPandit, setSelectedPandit] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [verifyAction, setVerifyAction] = useState(null); // 'verify' or 'reject'
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchPandits();
  }, [page, verificationLevel]);

  const fetchPandits = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      
      if (search) params.append('search', search);
      if (verificationLevel) params.append('verification_level', verificationLevel);

      const response = await axios.get(`${API}/admin/pandits?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPandits(response.data.pandits);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load pandits');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPandits();
  };

  const handleVerifyPandit = async () => {
    if (!selectedPandit) return;
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_admin_token');
      const isVerify = verifyAction === 'verify';
      
      await axios.put(
        `${API}/admin/pandits/${selectedPandit.id}/verify`,
        { 
          verified: isVerify,
          rejection_reason: !isVerify ? rejectionReason : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Pandit ${isVerify ? 'verified' : 'rejected'} successfully`);
      setShowVerifyModal(false);
      setSelectedPandit(null);
      setVerifyAction(null);
      setRejectionReason('');
      fetchPandits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const getVerificationBadge = (level, verified) => {
    if (verified) {
      return <Badge className="bg-green-100 text-green-700">Fully Verified</Badge>;
    }
    switch (level) {
      case 'identity':
        return <Badge className="bg-yellow-100 text-yellow-700">Identity Pending</Badge>;
      case 'knowledge':
        return <Badge className="bg-blue-100 text-blue-700">Exam Passed</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">Not Verified</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pandit Verification</h1>
          <p className="text-gray-500">Manage and verify pandit registrations</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="px-4 py-2 bg-yellow-50 rounded-lg">
            <span className="text-yellow-700 font-medium">
              {pandits.filter(p => p.verification_level === 'identity' && !p.verified).length} Pending
            </span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, city, or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          
          <select
            value={verificationLevel}
            onChange={(e) => { setVerificationLevel(e.target.value); setPage(1); }}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            {VERIFICATION_LEVELS.map(level => (
              <option key={level.value} value={level.value}>{level.label}</option>
            ))}
          </select>
          
          <Button type="submit" className="bg-[#FF9933] hover:bg-[#E68A00]">
            <Search className="w-4 h-4 mr-2" />
            Search
          </Button>
        </form>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{total}</div>
              <div className="text-xs text-gray-500">Total Pandits</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {pandits.filter(p => p.verified).length}
              </div>
              <div className="text-xs text-gray-500">Verified</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {pandits.filter(p => p.verification_level === 'identity' && !p.verified).length}
              </div>
              <div className="text-xs text-gray-500">Pending Review</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {pandits.filter(p => p.verification_level === 'none').length}
              </div>
              <div className="text-xs text-gray-500">Unverified</div>
            </div>
          </div>
        </div>
      </div>

      {/* Pandits Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF9933]" />
          </div>
        ) : pandits.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No pandits found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pandit</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Experience</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pandits.map((pandit) => (
                  <tr key={pandit.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={pandit.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(pandit.name)}&background=FF9933&color=fff&size=40`}
                          alt={pandit.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <div className="font-medium text-gray-900">{pandit.name}</div>
                          <div className="text-sm text-gray-500">{pandit.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-gray-600">
                        <MapPin className="w-4 h-4" />
                        <span>{pandit.city}, {pandit.state}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>{pandit.experience_years} years</span>
                      </div>
                      {pandit.qualification && (
                        <div className="text-xs text-gray-400">{pandit.qualification}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="font-medium">{pandit.rating?.toFixed(1) || 'New'}</span>
                        <span className="text-gray-400 text-sm">({pandit.reviews_count || 0})</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getVerificationBadge(pandit.verification_level, pandit.verified)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setSelectedPandit(pandit); setShowDetailModal(true); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {!pandit.verified && (
                          <>
                            <Button
                              size="sm"
                              className="bg-green-500 hover:bg-green-600"
                              onClick={() => { 
                                setSelectedPandit(pandit); 
                                setVerifyAction('verify'); 
                                setShowVerifyModal(true); 
                              }}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-500 border-red-200 hover:bg-red-50"
                              onClick={() => { 
                                setSelectedPandit(pandit); 
                                setVerifyAction('reject'); 
                                setShowVerifyModal(true); 
                              }}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-sm text-gray-500">
              Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pandit Profile Details</DialogTitle>
          </DialogHeader>
          
          {selectedPandit && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="flex items-start gap-4">
                <img
                  src={selectedPandit.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedPandit.name)}&background=FF9933&color=fff&size=80`}
                  alt={selectedPandit.name}
                  className="w-20 h-20 rounded-xl object-cover"
                />
                <div>
                  <h3 className="text-xl font-semibold">{selectedPandit.name}</h3>
                  <p className="text-gray-500">{selectedPandit.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {getVerificationBadge(selectedPandit.verification_level, selectedPandit.verified)}
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl">
                <div>
                  <label className="text-xs text-gray-500">Location</label>
                  <p className="font-medium">{selectedPandit.city}, {selectedPandit.state}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Phone</label>
                  <p className="font-medium">{selectedPandit.phone}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Experience</label>
                  <p className="font-medium">{selectedPandit.experience_years} years</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Qualification</label>
                  <p className="font-medium">{selectedPandit.qualification || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Base Fee</label>
                  <p className="font-medium">₹{selectedPandit.fees_per_puja}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Fee Range</label>
                  <p className="font-medium">₹{selectedPandit.fees_range_min} - ₹{selectedPandit.fees_range_max}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Total Bookings</label>
                  <p className="font-medium">{selectedPandit.total_bookings || 0}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Completed</label>
                  <p className="font-medium">{selectedPandit.completed_bookings || 0}</p>
                </div>
              </div>

              {/* Pooja Types */}
              <div>
                <label className="text-xs text-gray-500 block mb-2">Pooja Services</label>
                <div className="flex flex-wrap gap-2">
                  {selectedPandit.puja_types?.map((type, i) => (
                    <Badge key={i} variant="secondary">{type}</Badge>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <label className="text-xs text-gray-500 block mb-2">Languages</label>
                <div className="flex flex-wrap gap-2">
                  {selectedPandit.languages?.map((lang, i) => (
                    <Badge key={i} variant="outline">{lang}</Badge>
                  ))}
                </div>
              </div>

              {/* Identity Proof */}
              {selectedPandit.identity_proof_url && (
                <div>
                  <label className="text-xs text-gray-500 block mb-2">Identity Proof</label>
                  <a 
                    href={`${BACKEND_URL}${selectedPandit.identity_proof_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:underline"
                  >
                    <FileText className="w-4 h-4" />
                    View Document
                  </a>
                </div>
              )}

              {/* Actions */}
              {!selectedPandit.verified && (
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    className="flex-1 bg-green-500 hover:bg-green-600"
                    onClick={() => {
                      setShowDetailModal(false);
                      setVerifyAction('verify');
                      setShowVerifyModal(true);
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Verify Pandit
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 text-red-500 border-red-200 hover:bg-red-50"
                    onClick={() => {
                      setShowDetailModal(false);
                      setVerifyAction('reject');
                      setShowVerifyModal(true);
                    }}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Verify/Reject Modal */}
      <Dialog open={showVerifyModal} onOpenChange={setShowVerifyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {verifyAction === 'verify' ? 'Verify Pandit' : 'Reject Verification'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {verifyAction === 'verify' ? (
              <p className="text-gray-600">
                Are you sure you want to verify <strong>{selectedPandit?.name}</strong>? 
                This will mark them as a trusted pandit on the platform.
              </p>
            ) : (
              <div className="space-y-3">
                <p className="text-gray-600">
                  Please provide a reason for rejecting <strong>{selectedPandit?.name}</strong>'s verification:
                </p>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="e.g., Incomplete documentation, unverifiable credentials..."
                  rows={3}
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVerifyModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleVerifyPandit}
              disabled={actionLoading || (verifyAction === 'reject' && !rejectionReason)}
              className={verifyAction === 'verify' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : verifyAction === 'verify' ? (
                <CheckCircle className="w-4 h-4 mr-2" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              {verifyAction === 'verify' ? 'Verify' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
